// client/src/components/Sidebar.jsx
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import cancel from "../icons/cancel.png";
import locationLogo from "../assets/location-logo-blue.png";
import profileIcon from "../icons/profile.png";
import sidetabIcon from "../icons/sidetab.png";
import { v4 as uuidv4 } from 'uuid';

import { API_BASE1 } from "../config"; // adjust path if file is deeper
import axios from "axios"; // if you're using axios

// Backend base (match the rest of the app)
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env &&
    (import.meta.env.VITE_API_BASE || import.meta.env.REACT_APP_API_BASE)) ||
  process.env.REACT_APP_API_BASE ||
  `${API_BASE1}`;
  // "http://localhost:5050";

export default function Sidebar({ onClose, userId }) {
    const navigate = useNavigate();

    // send JWT if available; otherwise dev header
    const getAuthHeaders = (uid) => {
        const token = localStorage.getItem('token');
        return token
            ? { Authorization: `Bearer ${token}` }
            : { 'x-dev-user-id': uid }; // works if server runs with NODE_ENV=development
    };

    // Handle sign out: clear userId and redirect
    const handleSignOut = () => {
        localStorage.removeItem('userId');
        navigate('/signin');
        if (onClose) onClose();
    };

    const styles = {
        sidebar: {
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            width: '260px',
            backgroundColor: '#f7f7f8',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Inter, Arial, sans-serif',
            boxShadow: '2px 0 8px rgba(0,0,0,0.07)',
            zIndex: 1000,
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontWeight: 700,
            fontSize: '22px',
            padding: '24px 20px 12px 20px',
            background: '#fff',
            borderBottom: '1px solid #ececec',
        },
        closeIcon: {
            width: '18px',
            height: '18px',
            cursor: 'pointer',
            marginLeft: 'auto',
        },
        nav: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '18px 0 0 0',
        },
        navLink: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            textDecoration: 'none',
            color: '#222',
            fontSize: '16px',
            fontWeight: 500,
            padding: '10px 28px',
            borderRadius: '8px',
            transition: 'background 0.15s',
        },
        navLinkActive: { background: '#e6e6e9' },
        sectionTitle: {
            color: '#b0b0b0',
            fontSize: '13px',
            fontWeight: 600,
            margin: '18px 0 4px 28px',
            letterSpacing: '0.5px',
        },
        chatLink: {
            display: 'block',
            color: '#222',
            textDecoration: 'none',
            fontSize: '15px',
            padding: '7px 28px',
            borderRadius: '6px',
            marginBottom: '2px',
            transition: 'background 0.15s',
        },
        chatLinkActive: { background: '#ececec' },
        signOut: {
            color: '#e74c3c',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textDecoration: 'none',
            fontSize: '16px',
            padding: '18px 28px',
            borderTop: '1px solid #ececec',
            background: '#fff',
            marginTop: 'auto',
            cursor: 'pointer',
        },
    };

    // Chat sessions (history)
    const [sessions, setSessions] = useState([]);

    // Fallback to localStorage if prop is missing
    const effectiveUserId = userId || localStorage.getItem('userId');

    useEffect(() => {
        if (!effectiveUserId) return;
        // fetch chats tied to this user
        fetch(`${API_BASE}/api/chats?userId=${encodeURIComponent(effectiveUserId)}`, {
            headers: getAuthHeaders(effectiveUserId)
        })
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                const list = Array.isArray(data) ? data : (Array.isArray(data?.all) ? data.all : []);
                setSessions(list || []);
            })
            .catch(() => setSessions([]));
    }, [effectiveUserId]);

    // Create a new server-side session so it appears in history immediately.
    const handleNewChat = async () => {
        const uid = effectiveUserId;
        if (uid) {
            try {
                const res = await fetch(`${API_BASE}/api/chats`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders(uid) },
                    body: JSON.stringify({ userId: uid, title: 'New chat' }) // ✅ include userId
                });
                if (res.ok) {
                    const created = await res.json(); // { _id or sessionId, title }
                    const newId = created?.sessionId || created?._id;
                    if (newId) {
                        setSessions(prev => [{ sessionId: newId, title: created?.title || 'New chat' }, ...prev]);
                        navigate(`/chat/${newId}`);
                        if (onClose) onClose();
                        return;
                    }
                }
            } catch (_) { /* fall through */ }
        }
        // Fallback to local UUID; backend can upsert on first message.
        const newSessionId = uuidv4();
        navigate(`/chat/${newSessionId}`);
        if (onClose) onClose();
    };

    return (
        <div style={styles.sidebar}>
            <div style={styles.header}>
                <img src={locationLogo} alt="logo" style={{ width: 26, height: 26 }} />
                LocalGenie.ai
                <img src={cancel} alt="Close" style={styles.closeIcon} onClick={onClose} />
            </div>
            <nav style={styles.nav}>
                <button
                    type="button"
                    style={{ ...styles.navLink, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={handleNewChat}
                >
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.5 3.5C15.7652 3.23478 16.2348 3.23478 16.5 3.5L18.5 5.5C18.7652 5.76522 18.7652 6.23478 18.5 6.5L8 17H5V14L15.5 3.5Z" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    New chat
                </button>

                <Link to="/profile" style={styles.navLink} onClick={onClose}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="11" cy="8" r="4" stroke="#222" strokeWidth="1.5"/>
                            <path d="M4 19c0-3.31 3.13-6 7-6s7 2.69 7 6" stroke="#222" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </span>
                    My Profile
                </Link>

                {/* Favorites removed */}

                <div style={styles.sectionTitle}>Chat History</div>
                {sessions.length === 0 && (
                    <div style={{ color: '#bbb', paddingLeft: 28, fontSize: 14 }}>No chats</div>
                )}
                {sessions.map(s => (
                    <Link
                        key={s.sessionId || s._id}
                        to={`/chat/${s.sessionId || s._id}`}
                        style={styles.chatLink}
                        onClick={onClose}
                    >
                        {s.title || 'Chat'}
                    </Link>
                ))}
            </nav>

            <button type="button" style={styles.signOut} onClick={handleSignOut}>
                <span style={{ fontSize: 20 }}>⎋</span>
                Sign Out
            </button>
        </div>
    );
}






/*
// client/src/components/Sidebar.jsx
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import cancel from "../icons/cancel.png";
import locationLogo from "../assets/location-logo-blue.png";
import profileIcon from "../icons/profile.png";
import sidetabIcon from "../icons/sidetab.png";
import { v4 as uuidv4 } from 'uuid';

// Backend base (match the rest of the app)
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env &&
    (import.meta.env.VITE_API_BASE || import.meta.env.REACT_APP_API_BASE)) ||
  process.env.REACT_APP_API_BASE ||
  "http://localhost:5050";

export default function Sidebar({ onClose, userId }) {
    const navigate = useNavigate();

    // send JWT if available; otherwise dev header
    const getAuthHeaders = (uid) => {
        const token = localStorage.getItem('token');
        return token
            ? { Authorization: `Bearer ${token}` }
            : { 'x-dev-user-id': uid }; // works if server runs with NODE_ENV=development
    };

    // Handle sign out: clear userId and redirect
    const handleSignOut = () => {
        localStorage.removeItem('userId');
        navigate('/signin');
        if (onClose) onClose();
    };

    const styles = {
        sidebar: {
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            width: '260px',
            backgroundColor: '#f7f7f8',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Inter, Arial, sans-serif',
            boxShadow: '2px 0 8px rgba(0,0,0,0.07)',
            zIndex: 1000,
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontWeight: 700,
            fontSize: '22px',
            padding: '24px 20px 12px 20px',
            background: '#fff',
            borderBottom: '1px solid #ececec',
        },
        closeIcon: {
            width: '18px',
            height: '18px',
            cursor: 'pointer',
            marginLeft: 'auto',
        },
        nav: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '18px 0 0 0',
        },
        navLink: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            textDecoration: 'none',
            color: '#222',
            fontSize: '16px',
            fontWeight: 500,
            padding: '10px 28px',
            borderRadius: '8px',
            transition: 'background 0.15s',
        },
        navLinkActive: { background: '#e6e6e9' },
        sectionTitle: {
            color: '#b0b0b0',
            fontSize: '13px',
            fontWeight: 600,
            margin: '18px 0 4px 28px',
            letterSpacing: '0.5px',
        },
        chatLink: {
            display: 'block',
            color: '#222',
            textDecoration: 'none',
            fontSize: '15px',
            padding: '7px 28px',
            borderRadius: '6px',
            marginBottom: '2px',
            transition: 'background 0.15s',
        },
        chatLinkActive: { background: '#ececec' },
        signOut: {
            color: '#e74c3c',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textDecoration: 'none',
            fontSize: '16px',
            padding: '18px 28px',
            borderTop: '1px solid #ececec',
            background: '#fff',
            marginTop: 'auto',
            cursor: 'pointer',
        },
    };

    // Chat sessions (history)
    const [sessions, setSessions] = useState([]);

    // Fallback to localStorage if prop is missing
    const effectiveUserId = userId || localStorage.getItem('userId');

    useEffect(() => {
        if (!effectiveUserId) return;
        // fetch chats tied to this user
        fetch(`${API_BASE}/api/chats?userId=${encodeURIComponent(effectiveUserId)}`, {
            headers: getAuthHeaders(effectiveUserId)
        })
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                const list = Array.isArray(data) ? data : (Array.isArray(data?.all) ? data.all : []);
                setSessions(list || []);
            })
            .catch(() => setSessions([]));
    }, [effectiveUserId]);

    // Create a new server-side session so it appears in history immediately.
    const handleNewChat = async () => {
        const uid = effectiveUserId;
        if (uid) {
            try {
                const res = await fetch(`${API_BASE}/api/chats`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders(uid) },
                    body: JSON.stringify({ userId: uid, title: 'New chat' }) // ✅ include userId
                });
                if (res.ok) {
                    const created = await res.json(); // { _id or sessionId, title }
                    const newId = created?.sessionId || created?._id;
                    if (newId) {
                        setSessions(prev => [{ sessionId: newId, title: created?.title || 'New chat' }, ...prev]);
                        navigate(`/chat/${newId}`);
                        if (onClose) onClose();
                        return;
                    }
                }
            } catch (_) {  }
        }
        // Fallback to local UUID; backend can upsert on first message.
        const newSessionId = uuidv4();
        navigate(`/chat/${newSessionId}`);
        if (onClose) onClose();
    };

    return (
        <div style={styles.sidebar}>
            <div style={styles.header}>
                <img src={locationLogo} alt="logo" style={{ width: 26, height: 26 }} />
                LocalGenie.ai
                <img src={cancel} alt="Close" style={styles.closeIcon} onClick={onClose} />
            </div>
            <nav style={styles.nav}>
                <button
                    type="button"
                    style={{ ...styles.navLink, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={handleNewChat}
                >
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.5 3.5C15.7652 3.23478 16.2348 3.23478 16.5 3.5L18.5 5.5C18.7652 5.76522 18.7652 6.23478 18.5 6.5L8 17H5V14L15.5 3.5Z" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    New chat
                </button>

                <Link to="/profile" style={styles.navLink} onClick={onClose}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="11" cy="8" r="4" stroke="#222" strokeWidth="1.5"/>
                            <path d="M4 19c0-3.31 3.13-6 7-6s7 2.69 7 6" stroke="#222" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </span>
                    My Profile
                </Link>

                <Link to="/favorites" style={styles.navLink} onClick={onClose}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 19l-1.45-1.31C5.4 13.36 2 10.28 2 6.5 2 4.5 3.5 3 5.5 3c1.54 0 3.04 1.04 3.57 2.36h1.87C13.46 4.04 14.96 3 16.5 3 18.5 3 20 4.5 20 6.5c0 3.78-3.4 6.86-7.55 11.19L11 19z" stroke="#222" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    Favorites
                </Link>

                <div style={styles.sectionTitle}>Chat History</div>
                {sessions.length === 0 && (
                    <div style={{ color: '#bbb', paddingLeft: 28, fontSize: 14 }}>No chats</div>
                )}
                {sessions.map(s => (
                    <Link
                        key={s.sessionId || s._id}
                        to={`/chat/${s.sessionId || s._id}`}
                        style={styles.chatLink}
                        onClick={onClose}
                    >
                        {s.title || 'Chat'}
                    </Link>
                ))}
            </nav>

            <button type="button" style={styles.signOut} onClick={handleSignOut}>
                <span style={{ fontSize: 20 }}>⎋</span>
                Sign Out
            </button>
        </div>
    );
}
*/



/*
// client/src/components/Sidebar.jsx
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import cancel from "../icons/cancel.png";
import locationLogo from "../assets/location-logo-blue.png";
import profileIcon from "../icons/profile.png";
import sidetabIcon from "../icons/sidetab.png";
import { v4 as uuidv4 } from 'uuid';

// Backend base (match the rest of the app)
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env &&
    (import.meta.env.VITE_API_BASE || import.meta.env.REACT_APP_API_BASE)) ||
  process.env.REACT_APP_API_BASE ||
  "http://localhost:5050";

export default function Sidebar({ onClose, userId }) {
    const navigate = useNavigate();

    // send JWT if available; otherwise dev header
    const getAuthHeaders = (uid) => {
        const token = localStorage.getItem('token');
        return token
            ? { Authorization: `Bearer ${token}` }
            : { 'x-dev-user-id': uid }; // works if server runs with NODE_ENV=development
    };

    // Handle sign out: clear userId and redirect
    const handleSignOut = () => {
        localStorage.removeItem('userId');
        navigate('/signin');
        if (onClose) onClose();
    };

    const styles = {
        sidebar: {
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            width: '260px',
            backgroundColor: '#f7f7f8',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Inter, Arial, sans-serif',
            boxShadow: '2px 0 8px rgba(0,0,0,0.07)',
            zIndex: 1000,
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontWeight: 700,
            fontSize: '22px',
            padding: '24px 20px 12px 20px',
            background: '#fff',
            borderBottom: '1px solid #ececec',
        },
        closeIcon: {
            width: '18px',
            height: '18px',
            cursor: 'pointer',
            marginLeft: 'auto',
        },
        nav: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '18px 0 0 0',
        },
        navLink: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            textDecoration: 'none',
            color: '#222',
            fontSize: '16px',
            fontWeight: 500,
            padding: '10px 28px',
            borderRadius: '8px',
            transition: 'background 0.15s',
        },
        navLinkActive: { background: '#e6e6e9' },
        sectionTitle: {
            color: '#b0b0b0',
            fontSize: '13px',
            fontWeight: 600,
            margin: '18px 0 4px 28px',
            letterSpacing: '0.5px',
        },
        chatLink: {
            display: 'block',
            color: '#222',
            textDecoration: 'none',
            fontSize: '15px',
            padding: '7px 28px',
            borderRadius: '6px',
            marginBottom: '2px',
            transition: 'background 0.15s',
        },
        chatLinkActive: { background: '#ececec' },
        signOut: {
            color: '#e74c3c',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textDecoration: 'none',
            fontSize: '16px',
            padding: '18px 28px',
            borderTop: '1px solid #ececec',
            background: '#fff',
            marginTop: 'auto',
            cursor: 'pointer',
        },
    };

    // Chat sessions (history)
    const [sessions, setSessions] = useState([]);

    // Fallback to localStorage if prop is missing
    const effectiveUserId = userId || localStorage.getItem('userId');

    useEffect(() => {
        if (!effectiveUserId) return;
        // use backend base + auth headers
        fetch(`${API_BASE}/api/chats`, { headers: getAuthHeaders(effectiveUserId) })
            .then(r => r.ok ? r.json() : [])
            .then(data => {
                const list = Array.isArray(data) ? data : (Array.isArray(data?.all) ? data.all : []);
                setSessions(list || []);
            })
            .catch(() => setSessions([]));
    }, [effectiveUserId]);

    // Create a new server-side session so it appears in history immediately.
    const handleNewChat = async () => {
        const uid = effectiveUserId;
        if (uid) {
            try {
                const res = await fetch(`${API_BASE}/api/chats`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders(uid) },
                    body: JSON.stringify({ title: 'New chat' })
                });
                if (res.ok) {
                    const created = await res.json(); // { _id or sessionId, title }
                    const newId = created?.sessionId || created?._id;
                    if (newId) {
                        setSessions(prev => [{ sessionId: newId, title: created?.title || 'New chat' }, ...prev]);
                        navigate(`/chat/${newId}`);
                        if (onClose) onClose();
                        return;
                    }
                }
            } catch (_) { }
        }
        // Fallback to local UUID; backend can upsert on first message.
        const newSessionId = uuidv4();
        navigate(`/chat/${newSessionId}`);
        if (onClose) onClose();
    };

    return (
        <div style={styles.sidebar}>
            <div style={styles.header}>
                <img src={locationLogo} alt="logo" style={{ width: 26, height: 26 }} />
                LocalGenie.ai
                <img src={cancel} alt="Close" style={styles.closeIcon} onClick={onClose} />
            </div>
            <nav style={styles.nav}>
                <button
                    type="button"
                    style={{ ...styles.navLink, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={handleNewChat}
                >
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.5 3.5C15.7652 3.23478 16.2348 3.23478 16.5 3.5L18.5 5.5C18.7652 5.76522 18.7652 6.23478 18.5 6.5L8 17H5V14L15.5 3.5Z" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    New chat
                </button>

                <Link to="/profile" style={styles.navLink} onClick={onClose}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="11" cy="8" r="4" stroke="#222" strokeWidth="1.5"/>
                            <path d="M4 19c0-3.31 3.13-6 7-6s7 2.69 7 6" stroke="#222" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </span>
                    My Profile
                </Link>

                <Link to="/favorites" style={styles.navLink} onClick={onClose}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 19l-1.45-1.31C5.4 13.36 2 10.28 2 6.5 2 4.5 3.5 3 5.5 3c1.54 0 3.04 1.04 3.57 2.36h1.87C13.46 4.04 14.96 3 16.5 3 18.5 3 20 4.5 20 6.5c0 3.78-3.4 6.86-7.55 11.19L11 19z" stroke="#222" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    Favorites
                </Link>

                <div style={styles.sectionTitle}>Chat History</div>
                {sessions.length === 0 && (
                    <div style={{ color: '#bbb', paddingLeft: 28, fontSize: 14 }}>No chats</div>
                )}
                {sessions.map(s => (
                    <Link
                        key={s.sessionId || s._id}
                        to={`/chat/${s.sessionId || s._id}`}
                        style={styles.chatLink}
                        onClick={onClose}
                    >
                        {s.title || 'Chat'}
                    </Link>
                ))}
            </nav>

            <button type="button" style={styles.signOut} onClick={handleSignOut}>
                <span style={{ fontSize: 20 }}>⎋</span>
                Sign Out
            </button>
        </div>
    );
}
*/



/*
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import cancel from "../icons/cancel.png";
import locationLogo from "../assets/location-logo-blue.png";


// You may need to pass userId as a prop or get it from context/auth
import { v4 as uuidv4 } from 'uuid';

export default function Sidebar({ onClose }) {
    // Handle sign out: clear userId and redirect
    const handleSignOut = () => {
        localStorage.removeItem('userId');
        navigate('/signin');
        if (onClose) onClose();
    };
    const styles = {
        sidebar: {
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            width: '260px',
            backgroundColor: '#f7f7f8',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Inter, Arial, sans-serif',
            boxShadow: '2px 0 8px rgba(0,0,0,0.07)',
            zIndex: 1000,
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontWeight: 700,
            fontSize: '22px',
            padding: '24px 20px 12px 20px',
            background: '#fff',
            borderBottom: '1px solid #ececec',
        },
        closeIcon: {
            width: '18px',
            height: '18px',
            cursor: 'pointer',
            marginLeft: 'auto',
        },
        nav: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '18px 0 0 0',
        },
        navLink: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            textDecoration: 'none',
            color: '#222',
            fontSize: '16px',
            fontWeight: 500,
            padding: '10px 28px',
            borderRadius: '8px',
            transition: 'background 0.15s',
        },
        navLinkActive: {
            background: '#e6e6e9',
        },
        sectionTitle: {
            color: '#b0b0b0',
            fontSize: '13px',
            fontWeight: 600,
            margin: '18px 0 4px 28px',
            letterSpacing: '0.5px',
        },
        chatLink: {
            display: 'block',
            color: '#222',
            textDecoration: 'none',
            fontSize: '15px',
            padding: '7px 28px',
            borderRadius: '6px',
            marginBottom: '2px',
            transition: 'background 0.15s',
        },
        chatLinkActive: {
            background: '#ececec',
        },
        signOut: {
            color: '#e74c3c',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            textDecoration: 'none',
            fontSize: '16px',
            padding: '18px 28px',
            borderTop: '1px solid #ececec',
            background: '#fff',
            marginTop: 'auto',
            cursor: 'pointer',
        },
    };

    // Only show unique chat sessions (history), not every message
    const [sessions, setSessions] = useState([]);

    const navigate = useNavigate();

    useEffect(() => {
        // Hardcoded userId for demo
        const userId = '68a7dd0c9a9062f0aeaddc3b';
        fetch(`/api/messages/sessions?userId=${userId}`)
            .then(r => r.json())
            .then(data => {
                setSessions(Array.isArray(data.all) ? data.all : []);
            })
            .catch(() => setSessions([]));
    }, []);

    // Handler for New Chat (like ChatGPT)
    const handleNewChat = () => {
        const newSessionId = uuidv4();
        navigate(`/chat/${newSessionId}`);
        if (onClose) onClose();
    };

    return (
        <div style={styles.sidebar}>
            <div style={styles.header}>
                <img src={locationLogo} alt="logo" style={{ width: 26, height: 26 }} />
                LocalGenie.ai
                <img src={cancel} alt="Close" style={styles.closeIcon} onClick={onClose} />
            </div>
            <nav style={styles.nav}>
                <button type="button" style={{...styles.navLink, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer'}} onClick={handleNewChat}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                        
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.5 3.5C15.7652 3.23478 16.2348 3.23478 16.5 3.5L18.5 5.5C18.7652 5.76522 18.7652 6.23478 18.5 6.5L8 17H5V14L15.5 3.5Z" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    New chat
                </button>
                <Link to="/profile" style={styles.navLink} onClick={onClose}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                        
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="11" cy="8" r="4" stroke="#222" strokeWidth="1.5"/>
                            <path d="M4 19c0-3.31 3.13-6 7-6s7 2.69 7 6" stroke="#222" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </span>
                    My Profile
                </Link>
                <Link to="/favorites" style={styles.navLink} onClick={onClose}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 8 }}>
                       
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 19l-1.45-1.31C5.4 13.36 2 10.28 2 6.5 2 4.5 3.5 3 5.5 3c1.54 0 3.04 1.04 3.57 2.36h1.87C13.46 4.04 14.96 3 16.5 3 18.5 3 20 4.5 20 6.5c0 3.78-3.4 6.86-7.55 11.19L11 19z" stroke="#222" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
                        </svg>
                    </span>
                    Favorites
                </Link>
                <div style={styles.sectionTitle}>Chat History</div>
                {sessions.length === 0 && <div style={{ color: '#bbb', paddingLeft: 28, fontSize: 14 }}>No chats</div>}
                {sessions.map(s => (
                    <Link key={s.sessionId} to={`/chat/${s.sessionId}`} style={styles.chatLink} onClick={onClose}>
                        {s.title || 'Chat'}
                    </Link>
                ))}
            </nav>
            <button type="button" style={styles.signOut} onClick={handleSignOut}>
                <span style={{ fontSize: 20 }}>⎋</span>
                Sign Out
            </button>
        </div>
    );
}
*/